import React from 'react';
import LoadingSpinner from './LoadingSpinner';
import { RefreshCw, CheckCircle, Info, Radar, BellRing } from 'lucide-react';

// Shared by KB Web Scraper (saved website view) and Website Monitoring

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : 'Never');
const cardClass = 'bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant';
const secondaryBtn = 'px-4 py-2 rounded-lg text-sm font-medium border border-border/60 text-foreground hover:border-primary/40 hover:bg-muted/40 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed';

const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none text-sm font-medium text-foreground">
    <input type="checkbox" className="w-4 h-4 accent-[hsl(var(--primary))]" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

// One-line status of the free daily change check
export const monitorInfo = (site) => {
  const n = site.monitor_counts || {};
  if (!site.monitor_enabled) return { text: 'Not watching for changes', className: 'text-muted-foreground' };
  if (site.monitor_checking) return { text: 'Checking the website for changes...', className: 'text-primary' };
  switch (site.monitor_status) {
    case 'changed': {
      if (site.monitor_summary) return { text: `Website changed: ${site.monitor_summary} Sync to update the files.`, className: 'text-warning' };
      const parts = [];
      if (n.important) parts.push(`${n.important} page${n.important === 1 ? '' : 's'} with important changes`);
      if (n.gone) parts.push(`${n.gone} page${n.gone === 1 ? '' : 's'} removed from the site`);
      return { text: `Website changed since the last sync: ${parts.join(', ')}. Sync to update the files.`, className: 'text-warning' };
    }
    case 'minor_changes':
      return { text: `Only minor edits since the last sync, nothing the agent needs · checked ${formatDate(site.monitor_checked_at)}`, className: 'text-muted-foreground' };
    case 'unmonitorable':
      return { text: 'Cannot watch for changes: these pages are built by JavaScript in the browser', className: 'text-muted-foreground' };
    case 'baseline':
    case 'unchanged':
      return { text: `No changes since the last sync · checked ${formatDate(site.monitor_checked_at)}`, className: 'text-muted-foreground' };
    case 'error':
      return { text: `The last change check failed · ${formatDate(site.monitor_checked_at)}`, className: 'text-muted-foreground' };
    default:
      return { text: 'Watching for changes. The first check runs within a day.', className: 'text-muted-foreground' };
  }
};

const ChangedLines = ({ label, lines, total, tone }) => (lines?.length ? (
  <div>
    <div className={`text-[11px] font-semibold ${tone === 'add' ? 'text-success' : 'text-destructive'}`}>{label} ({total})</div>
    <ul className="mt-1 space-y-0.5">
      {lines.map((l, i) => (
        <li key={i} className={`text-xs font-mono px-2 py-0.5 rounded break-words ${tone === 'add' ? 'bg-success/10' : 'bg-destructive/10 line-through decoration-destructive/50'}`}>{l}</li>
      ))}
      {total > lines.length && <li className="text-[11px] text-muted-foreground">and {total - lines.length} more</li>}
    </ul>
  </div>
) : null);

const IMPORTANCE = {
  important: { label: 'Important', className: 'bg-warning/20 text-warning' },
  minor: { label: 'Minor', className: 'bg-muted/60 text-muted-foreground' },
  ignore: { label: 'Noise', className: 'bg-muted/60 text-muted-foreground' },
};

export const MonitorCard = ({ site, onCheck, onSeen, onToggle, onSync, syncLabel, busy, canSync, children }) => {
  const info = monitorInfo(site);
  const changes = site.monitor_changes || {};
  const changed = site.monitor_status === 'changed';
  const flagged = changed || site.monitor_status === 'minor_changes';
  const ai = changes.ai;
  return (
    <div className={`${cardClass} ${changed ? 'border-warning/50' : ''}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            {changed ? <BellRing className="w-5 h-5 text-warning" /> : <Radar className="w-5 h-5 text-primary" />} Website changes
          </h3>
          <p className={`text-sm mt-1 ${info.className}`}>{info.text}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Checked once a day by reading the saved pages directly. Free: it never uses BrowserAct credits and never syncs on its own.
            When something changed, AI reads just the changed lines and decides whether it matters to the agent. Prices, phone numbers, emails and hours always count.
          </p>
          {ai && (ai.model || ai.cached > 0 || ai.error) && (
            <p className="text-[11px] text-muted-foreground mt-1">
              {ai.error ? `AI review unavailable (${ai.error}), so every change is treated as important.`
                : ai.model ? `Reviewed by ${ai.model} for about $${(ai.cost_usd || 0).toFixed(3)}.`
                  : 'Same changes as the previous check, so the earlier AI review was reused at no cost.'}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {flagged && (
            <>
              {onSync && (
                <button onClick={onSync} disabled={!canSync} className={secondaryBtn}>
                  <RefreshCw className="w-4 h-4" /> {syncLabel || 'Sync now'}
                </button>
              )}
              <button onClick={onSeen} disabled={busy || site.monitor_checking} className={secondaryBtn} title="Keep the current files and stop flagging these changes">
                <CheckCircle className="w-4 h-4" /> Mark as seen
              </button>
            </>
          )}
          <button onClick={onCheck} disabled={busy || site.monitor_checking || !site.monitor_enabled} className={secondaryBtn}>
            {site.monitor_checking ? <LoadingSpinner size="sm" /> : <Radar className="w-4 h-4" />} Check now
          </button>
          <Toggle checked={site.monitor_enabled} onChange={onToggle} label="Watch daily" />
        </div>
      </div>
      {children}

      {site.monitor_enabled && (changes.pages?.length > 0 || changes.gone?.length > 0) && (
        <div className="mt-4 space-y-3">
          {changes.pages?.map((p) => (
            <div key={p.url} className="rounded-lg border border-border/60 p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  {p.importance && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${IMPORTANCE[p.importance]?.className || ''}`} title={p.reason}>
                      {IMPORTANCE[p.importance]?.label || p.importance}
                    </span>
                  )}
                  <a href={p.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-primary break-all">{p.name}</a>
                </div>
                <span className="text-[11px] font-mono"><span className="text-success">+{p.added_count}</span> <span className="text-destructive">-{p.removed_count}</span></span>
              </div>
              {p.summary && <p className="text-sm text-foreground mt-1.5">{p.summary}</p>}
              {p.reason && <p className="text-[11px] text-muted-foreground mt-0.5">Why: {p.reason}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <ChangedLines label="New on the website" lines={p.added} total={p.added_count} tone="add" />
                <ChangedLines label="No longer on the website" lines={p.removed} total={p.removed_count} tone="del" />
              </div>
            </div>
          ))}
          {changes.gone?.length > 0 && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs">
              <p className="font-semibold text-foreground mb-1">Pages that now return "not found":</p>
              <ul className="space-y-0.5">{changes.gone.map((p) => <li key={p.url} className="break-all text-muted-foreground">{p.name}: {p.url}</li>)}</ul>
              <p className="text-muted-foreground mt-1">Use Edit pages to remove them, or the sync will report them as failed.</p>
            </div>
          )}
        </div>
      )}

      {site.monitor_enabled && changes.new_pages?.length > 0 && (
        <div className="mt-3 rounded-lg border border-border/60 bg-muted/20 p-3 text-xs">
          <p className="font-semibold text-foreground mb-1 flex items-center gap-1.5"><Info className="w-3.5 h-3.5 text-primary" /> {changes.new_pages_total} new page{changes.new_pages_total === 1 ? '' : 's'} in the website's sitemap, not in your selection</p>
          <ul className="space-y-0.5">{changes.new_pages.map((u) => <li key={u} className="break-all text-muted-foreground">{u}</li>)}</ul>
          <p className="text-muted-foreground mt-1">For information only. Use Edit pages to add any you want in the knowledge base.</p>
        </div>
      )}

      {site.monitor_enabled && (changes.unmonitorable?.length > 0 || changes.unreachable?.length > 0) && site.monitor_status !== 'unmonitorable' && (
        <p className="text-xs text-muted-foreground mt-3">
          {changes.unmonitorable?.length > 0 && <>Not watched (built by JavaScript): {changes.unmonitorable.map((p) => p.name).join(', ')}. </>}
          {changes.unreachable?.length > 0 && <>Could not be reached on the last check: {changes.unreachable.map((p) => p.name).join(', ')}.</>}
        </p>
      )}
    </div>
  );
};
